import axios from 'axios';
import axiosRetry from 'axios-retry';
import defaultsDeep from 'lodash/defaultsDeep';
import isEmpty from 'lodash/isEmpty';
import queryString from 'querystring';
import { from } from 'rxjs';

// Shared axios instance with retry support (retries configured per-request via 'axios-retry' config key)
export const httpClient = axios.create();
axiosRetry(httpClient, { retries: 0 });

const sub = (val, substitutions) => substitutions[val] || val;

/**
 * Client for the NERF API gateway. Implemented as a "universal/isomorphic"
 * class that can be used on both server and browser.
 */
class ApiClient {
  /**
   * Constructor for ApiClient
   * @usage: `new ApiClient(options)`
   *
   * @param {object} options
   *    @property {string} apiGatewayURL  - Base URL of the API gateway
   *    @property {string} subdomain      - The subdomain of the current library
   *    @property {string} locale         - The current locale
   *    @property {object} headers        - Custom headers to send (e.g. cookie, user-agent,
   *                                          x-forwarded-for, x-requested-with)
   *
   */
  constructor({ apiGatewayURL, subdomain, locale, sessionId, authToken, headers = {} }) {
    Object.assign(this, {
      apiGatewayURL,
      sessionId,
      authToken,
      requestLocale: locale,
      requestHeaders: headers,
      _server: !!__SERVER__,
      _routeSubstitutions: {
        ':librarySubdomain': subdomain
      }
    });
  }

  get(path, options = {}) {
    return this._request('get', path, null, options);
  }

  post(path, data = {}, options = {}) {
    return this._request('post', path, data, options);
  }

  patch(path, data = {}, options = {}) {
    return this._request('patch', path, data, options);
  }

  put(path, data = {}, options = {}) {
    return this._request('put', path, data, options);
  }

  delete(path, data = {}, options = {}) {
    return this._request('delete', path, data, options);
  }

  /**
   * Helper method for constructing full URL
   *
   * @param  {object} path    - base path of the URL like /search or /item/show
   * @param  {object} options - additional url properties:
   * @param  {string} options.params - the dynamic part of the path eg. /item/show/:id
   * @param  {object} options.query  - Object which converts into query string
   * @return {string}
   */
  getFullUrl(path, options = {}) {
    const { params, query } = defaultsDeep(options, {
      query: this.requestLocale ? { locale: this.requestLocale } : {}
    });

    const queryStr = isEmpty(query) ? '' : `?${queryString.stringify(query)}`;
    return `${this.apiGatewayURL}${path || ''}${params || ''}${queryStr}`;
  }

  /**
   * Template tag function that builds a route string from a template
   * literal, with support for "magic" substitution of contextual params.
   *
   * @see `ApiClient#routeSubstitutions`.
   *
   * @example
   *   const apiClient = new ApiClient([options]);
   *   const bibId     = '12345';
   *   const route     = apiClient.route `/libraries/${':libraryDomain'}/bibs/${someBibId}`;
   *   console.info(route); // '/libraries/opl/bibs/12345'
   *
   * @implements {TagFunction}
   * @return {string} the interpolated path
   */
  route(segments, ...values) {
    let path = segments[0];
    values.forEach((val, i) => {
      path += sub(val, this._routeSubstitutions) + segments[i + 1];
    });
    return path;
  }

  /**
   * Makes a request to the API
   * @param  {string} method  HTTP method
   * @param  {string} path    relative path of API endpoint
   * @param  {object} options
   * @return {Observable}     Observable which will emit the response
   */
  _request(method, path, data = null, options = {}) {
    const headers = {
      Accept: options.Accept || 'application/json',
      ...(this.sessionId && this.authToken ? { 'X-Session-Id': this.sessionId, 'X-Access-Token': this.authToken } : {}),
      ...Object.fromEntries(Object.entries(this.requestHeaders).filter(([, val]) => val))
    };

    return from(
      httpClient
        .request({
          method,
          url: this.getFullUrl(path, options),
          data: data || undefined,
          headers,
          withCredentials: !this._server,
          'axios-retry': { retries: options.numRetries || 0 }
        })
        .then(response => ({ ...response, body: response.data }))
    );
  }
}

export default ApiClient;
